import React, { type JSX } from "react";

const LABEL = "text-sm font-medium text-gray-500 uppercase tracking-wider";

/** A labelled value in one of the modal's definition lists. */
export function Field({
  label,
  valueClass = "text-base text-gray-900",
  spacedLabel = false,
  children,
}: {
  label: string;
  valueClass?: string;
  spacedLabel?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <dt className={spacedLabel ? `${LABEL} mb-2` : LABEL}>{label}</dt>
      <dd className={`mt-1 ${valueClass}`}>{children}</dd>
    </div>
  );
}

/** Format an array field as a comma-separated string. */
export function formatArray(value: (string | null | undefined)[] | null | undefined): string {
  if (!value || value.length === 0) return "--";
  return value.join(", ");
}

/** Parse the "NAME as ROLE" format the deal API returns. */
function parseNameRole(entry: string): { name: string; role: string } {
  const asMatch = entry.match(/^(.+?)\s+as\s+(.+)$/i);
  if (asMatch) {
    return { name: asMatch[1].trim(), role: asMatch[2].trim() };
  }
  return { name: entry, role: "" };
}

/**
 * People and advisors as a name/role table, or as a plain list when none of
 * the entries carry a role.
 */
export function PeopleTable({ items }: { items: string[] | null | undefined }): JSX.Element {
  if (!items || items.length === 0) {
    return <span className="text-gray-500">--</span>;
  }

  const parsed = items.map(parseNameRole);
  if (!parsed.some((person) => person.role)) {
    return <span>{items.join(", ")}</span>;
  }

  return (
    <table className="min-w-full text-sm">
      <thead>
        <tr className="border-b border-gray-200">
          <th className="text-left py-1 pr-4 font-medium text-gray-600">Name</th>
          <th className="text-left py-1 font-medium text-gray-600">Role</th>
        </tr>
      </thead>
      <tbody>
        {parsed.map((item, idx) => (
          <tr key={idx} className="border-b border-gray-100 last:border-0">
            <td className="py-1 pr-4">{item.name}</td>
            <td className="py-1 text-gray-600">{item.role || "--"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
